import Navbar from "@/components/unsf/Navbar"
import Footer from "@/components/unsf/Footer"
import UnsfHead, { UnsfMeta } from "@/components/unsf/UnsfHead"
import type {
  UnsfNavbarProps,
  UnsfFooterProps,
} from "@/components/unsf/LegalLayout"

interface Props {
  meta: UnsfMeta
  title: string
  email: { label: string; address: string }
  address: { label: string; lines: string[] }
  response_time: string
  navbar: UnsfNavbarProps
  footer: UnsfFooterProps
  csrf_token: string
  cart: import("@/components/unsf/Cart").CartData
}

export default function Contact({
  meta,
  title,
  email,
  address,
  response_time,
  navbar,
  footer,
  csrf_token,
  cart,
}: Props) {
  const breadcrumbs = [
    { name: "UNSF Clothing", url: meta.canonical_url.replace(/\/[^/]*$/, "/") },
    { name: title, url: meta.canonical_url },
  ]

  return (
    <>
      <UnsfHead meta={meta} breadcrumbs={breadcrumbs} />

      <Navbar
        isHome={false}
        logoWhite={navbar.logo_white}
        logoBlack={navbar.logo_black}
        labels={navbar.labels}
        urls={navbar.urls}
        locale={{
          current: navbar.locale.current,
          other: navbar.locale.other,
          switchUrl: navbar.locale.switch_url,
        }}
        csrfToken={csrf_token}
        cart={cart}
      />

      <div className="min-h-screen bg-light pb-12 pt-24">
        <div className="container mx-auto px-6 max-w-2xl">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-contrast mb-4 font-unsf-title">
              {title}
            </h1>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="mb-10">
              <h2 className="text-xl font-bold text-contrast mb-4 font-unsf-title">
                {email.label}
              </h2>
              <div className="flex items-center gap-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-5 h-5 text-content"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                  />
                </svg>
                <a
                  href={`mailto:${email.address}`}
                  className="text-content hover:text-contrast transition-colors underline"
                >
                  {email.address}
                </a>
              </div>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-bold text-contrast mb-4 font-unsf-title">
                {address.label}
              </h2>
              <div className="flex items-start gap-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-5 h-5 text-content mt-0.5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
                  />
                </svg>
                <div className="text-content">
                  {address.lines.map((line, idx) => (
                    <div key={idx}>{line}</div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-light rounded-xl p-6 text-center">
              <p className="text-content text-sm">{response_time}</p>
            </div>
          </div>
        </div>
      </div>

      <Footer
        logoWhite={footer.logo_white}
        labels={footer.labels}
        urls={footer.urls}
      />
    </>
  )
}
